import {
  autoUpdate,
  offset,
  type Placement,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';

const VIEWPORT_PADDING = 8;

interface SetupMenuFloatingParams {
  popperElement: HTMLElement | null;
  referenceElement: HTMLElement | null;
  options?: SetupMenuFloatingOptions;
}

interface SetupMenuFloatingOptions {
  offset?: 'infinity' | 'overlap';
  placement?: Placement;
}

/**
 * Hook that sets up floating menu positioning using Floating UI
 * @param params Object containing popper element, reference element, and infinity mode flag
 * @returns Floating UI configuration object
 */
export const useSetupMenuFloating = ({
  popperElement,
  referenceElement,
  options,
}: SetupMenuFloatingParams) => {
  return useFloating({
    elements: {
      floating: popperElement,
      reference: referenceElement,
    },
    placement: options?.placement ?? 'right',
    strategy: 'fixed',
    middleware: [
      offset(({ rects }) => {
        // When in "infinity" mode, the menu should be centered above the "reference" element
        if (options?.offset === 'infinity') {
          return {
            mainAxis: -0.5 * (rects.floating.width + rects.reference.width),
          };
        }

        // menu can be placed to the right of the "reference" element, slightly overlapping it
        if (options?.offset === 'overlap') {
          return {
            mainAxis: -8,
          };
        }

        // By default, the menu keeps a small gap from the "reference" element
        return {
          mainAxis: 8,
        };
      }),
      // `crossAxis` is what lets a menu slide back over its reference: for a
      // `right` placement the cross axis is the horizontal one, and without it
      // shift only ever moves the menu vertically. The reference is always on
      // the left of the page (or covers the frame in the extension), so sliding
      // across it is right where flipping to the other side would not be.
      shift({ padding: VIEWPORT_PADDING, crossAxis: true }),
      // After shift, so it only trims what is left over once the menu has
      // already moved as far as it can.
      size({
        padding: VIEWPORT_PADDING,
        apply: ({ availableWidth, availableHeight, elements }) => {
          Object.assign(elements.floating.style, {
            maxWidth: `${Math.max(availableWidth, 0)}px`,
            maxHeight: `${Math.max(availableHeight, 0)}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
};
